import {fire} from 'delegated-events'
import {onFocus, onInput} from '@github-ui/onfocus'
import {observe} from '@github-ui/selector-observer'
import {requestSubmit} from '@github-ui/form-utils'
import {updateUrl} from '@github-ui/history'

const dataStore = new WeakMap()

// The maximum number of times we should load another page of repositories automatically, just
// when the user wants to filter the list of repositories
const autoLoadLimit = 100

// Submits the "Load more" form to load another page of repositories into the DOM
function loadMoreRepositories(container: HTMLElement) {
  const timesLoaded = dataStore.get(container) || 0
  if (timesLoaded > autoLoadLimit) {
    return
  }
  const form = container.querySelector<HTMLFormElement>('.js-more-repos-form')
  if (form) {
    dataStore.set(container, timesLoaded + 1)
    requestSubmit(form)
  }
}

// Updates a given URL string to have queryName (ex: `q=`) in it with the given text query
function replaceQueryInUrl(baseUrl: string, query: string, queryName: string) {
  const url = new URL(baseUrl, window.location.origin)
  const params = new URLSearchParams(url.search.slice(1))
  if (query.length < 1) {
    params.delete(queryName)
  } else {
    params.set(queryName, query)
  }
  url.search = params.toString()
  return url.toString()
}

// Should we submit the "Load more" form as many times as it takes to load all the user or org's
// repositories into the page, so we can do in-page filtering?
function shouldLoadAllRepositories(container: HTMLElement) {
  const filterInput = container.querySelector<HTMLInputElement>('.js-your-repositories-search')
  if (!filterInput) {
    // No need to load all the repos into the page if they can't be filtered
    return false
  }
  // Does the text input have the user's focus?
  if (document.activeElement === filterInput) {
    return true
  }
  // Does the text input already have a query loaded into it?
  return filterInput.defaultValue.trim().length > 0
}

function mergeLists(container: HTMLElement) {
  const reposLists = Array.from(container.getElementsByClassName('js-dashboard-repos-list')) as HTMLElement[]
  for (let i = 1; i < reposLists.length; i++) {
    const current = reposLists[i]
    if (!current) continue
    const items = Array.from(current.children)
    if (items.length && reposLists[0]) {
      reposLists[0].append(...items)
      current.remove()
    }
  }
}

// Filters the list of repositories in the given container based on the value of the filter input
function filterRepositories(container: HTMLElement) {
  const filterInput = container.querySelector('.js-your-repositories-search')
  if (filterInput) {
    mergeLists(container)
    fire(filterInput, 'filterable:change')
  }
}

// Push new queries into the URL so the user can return to this exact filter later
function updateQueryInUrl(filterInput: HTMLInputElement) {
  const baseUrl = filterInput.getAttribute('data-url')!
  const queryName = filterInput.getAttribute('data-query-name')!
  updateUrl(replaceQueryInUrl(baseUrl, filterInput.value.trim(), queryName))
}

// Temporarily suppress the next `filterable:change` fired for `input`.
function suppressNextFilterableChangeOn(input: HTMLElement) {
  const handler = (ev: Event) => {
    if (ev.target === input) {
      ev.stopImmediatePropagation()
      ev.stopPropagation()
    }
  }

  document.addEventListener('filterable:change', handler, true)

  setTimeout(() => document.removeEventListener('filterable:change', handler, true), 0)
}

// Usage: call before the operation that triggers focus-firing.
// Example: in your existing onFocus handler where you load and merge lists:
onFocus('.js-your-repositories-search', function (filterInput) {
  const container = filterInput.closest<HTMLElement>('.js-repos-container')!

  // Prevent the `filterable:change` handling for this focus (next event only)
  suppressNextFilterableChangeOn(filterInput)

  loadMoreRepositories(container)
})

// Filter repositories as soon as the page loads if there are no more pages of results to load and
// the filter input comes with a query already
observe('.js-your-repositories-search', {
  constructor: HTMLInputElement,
  initialize(filterInput) {
    if (filterInput.defaultValue.trim().length > 0) {
      filterRepositories(filterInput.closest<HTMLElement>('.js-repos-container')!)
    }
  },
})

onInput('.js-your-repositories-search', function (event) {
  const filterInput = event.target as HTMLInputElement
  updateQueryInUrl(filterInput)
})

observe('.js-more-repos-form', function (form) {
  // When the page loads and we see there's a "Load more" form, check to see if we should submit it
  // to load more repositories into the page
  const container = form.closest<HTMLElement>('.js-repos-container')!
  if (shouldLoadAllRepositories(container)) {
    loadMoreRepositories(container)
  }

  // When a page of repositories is loaded into the DOM, update the filtering in case the user has
  // typed a query into the text input
  form.addEventListener('page:loaded', function () {
    filterRepositories(container)
  })
})
